// services/messageTemplates.service.js
import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';

const client = generateClient();

/**
 * Fetch all message templates
 * @returns {Promise<Array>} - Array of message templates
 */
export const fetchAllMessageTemplates = async () => {
  const result = await client.graphql({
    query: queries.listMessageTemplates
  });
  return result.data.listMessageTemplates.items || [];
};

/**
 * Fetch message templates by type
 * @param {string} type - Template type
 * @returns {Promise<Array>} - Array of message templates of the specified type
 */
export const fetchMessageTemplatesByType = async (type) => {
  const result = await client.graphql({
    query: queries.messageTemplatesByType,
    variables: { type }
  });
  return result.data.messageTemplatesByType.items || [];
};

/**
 * Get active message template by type
 * @param {string} type - Template type
 * @returns {Promise<Object|null>} - Active message template or null if none found
 */
export const getActiveMessageTemplate = async (type) => {
  const templates = await fetchMessageTemplatesByType(type);
  return templates.find(template => template.isActive) || null;
};

/**
 * Create a new message template
 * @param {Object} input - Template data
 * @returns {Promise<Object>} - Created template
 */
export const createMessageTemplate = async (input) => {
  const result = await client.graphql({
    query: mutations.createMessageTemplate,
    variables: { input }
  });
  return result.data.createMessageTemplate;
};

/**
 * Update an existing message template
 * @param {Object} input - Template data with id
 * @returns {Promise<Object>} - Updated template
 */
export const updateMessageTemplate = async (input) => {
  const result = await client.graphql({
    query: mutations.updateMessageTemplate,
    variables: { input }
  });
  return result.data.updateMessageTemplate;
};

/**
 * Delete a message template
 * @param {string} id - Template ID
 * @returns {Promise<Object>} - Deleted template
 */
export const deleteMessageTemplate = async (id) => {
  const result = await client.graphql({
    query: mutations.deleteMessageTemplate,
    variables: { input: { id } }
  });
  return result.data.deleteMessageTemplate;
};

/**
 * Set a template as active and deactivate all other templates of the same type
 * @param {string} id - ID of the template to activate
 * @param {string} type - Template type
 * @returns {Promise<Object>} - Activated template
 */
export const setActiveTemplate = async (id, type) => {
  // First, deactivate all templates of this type
  const templates = await fetchMessageTemplatesByType(type);
  
  const updatePromises = templates
    .filter(template => template.id !== id && template.isActive)
    .map(template => 
      updateMessageTemplate({
        id: template.id,
        isActive: false
      })
    );
  
  // Wait for all deactivations to complete
  await Promise.all(updatePromises);
  
  // Then activate the selected template
  const result = await updateMessageTemplate({
    id,
    isActive: true
  });
  
  return result;
};
